/** The PDF control of one issued filing: a link that works with or without this script, and a state
 * a person can see while a render is under way (gate 24, ADR-0078).
 *
 * **Only ever rendered once the deployment has said it can print** (`usePdfCapability`), so it
 * never offers a control that predictably fails, and only for a filing that has been issued - the
 * list it sits in lists nothing else.
 */

import type { MouseEvent } from "react";

import { apiUrl } from "../api/client";
import { usePdfDownload } from "../api/pdfDownload";
import styles from "./Screen.module.css";
import own from "./Reports.module.css";

export function FilingPdf({ filingId }: { filingId: string }) {
  const { state, start } = usePdfDownload(filingId);

  function onClick(event: MouseEvent<HTMLAnchorElement>) {
    // A modified or non-primary click is the reader choosing a new tab, a window or save-as: the
    // browser's own handling reaches the same route and needs nothing from us.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    void start();
  }

  return (
    <>
      <a
        className={styles.fixLink}
        href={apiUrl(`/api/v1/artifacts/${filingId}/pdf`)}
        target="_blank"
        rel="noreferrer"
        data-testid="filing-pdf"
        aria-busy={state.phase === "preparing"}
        onClick={onClick}
      >
        {state.phase === "preparing" ? "Preparing PDF…" : "PDF"}
      </a>
      {/* One live region for every state, so a screen reader hears "Preparing" and then the result
          without the reader having to find it. Empty while idle, and never a second copy of the
          sentence the failure carries. */}
      <span className={own.fine} role="status" aria-live="polite" data-testid="filing-pdf-status">
        {state.phase === "preparing"
          ? "Preparing the PDF. This takes a few seconds."
          : state.phase === "saved"
            ? "PDF saved."
            : ""}
      </span>
      {state.phase === "failed" ? (
        <span className={own.fine} role="alert" data-testid="filing-pdf-error" data-code={state.code}>
          {state.message}
        </span>
      ) : null}
    </>
  );
}
